import { useEffect, useMemo, useState } from 'react';
import holidayFallback from '../data/holidaysFallback.json';

const normalizeHoliday = (item = {}) => {
  const inferredType = item.type
    || (item.calendar === 'solar' ? 'solar' : item.calendar === 'lunar' ? 'lunar' : undefined);
  const solarDate = item.solarDate
    || (inferredType === 'solar' && item.month && item.day ? `${item.month}-${item.day}` : undefined);
  const lunarMonth = item.lunar_month || item.lunarMonth;
  const lunarDay = item.lunar_day || item.lunarDay;
  const lunarDate = item.lunarDate
    || (inferredType === 'lunar' && lunarMonth && lunarDay ? `${lunarMonth}-${lunarDay}` : undefined);

  return {
    ...item,
    code: item.code || item.id,
    type: inferredType,
    solarDate,
    lunarDate
  };
};

const isValidHoliday = (item) => {
  if (!item || typeof item !== 'object') return false;
  const typeOk = item.type === 'solar' || item.type === 'lunar';
  const nameOk = typeof item.name_vi === 'string' || typeof item.name_en === 'string';
  const dateOk = Boolean(item.solarDate || item.lunarDate);
  const codeOk = typeof item.code === 'string' || typeof item.code === 'number';
  return typeOk && nameOk && dateOk && codeOk;
};

const normalizeHolidayList = (items) => {
  const fallbackSource = Array.isArray(items)
    ? items
    : items?.items || items?.holidays || [];

  return (Array.isArray(fallbackSource) ? fallbackSource : [])
    .map(normalizeHoliday)
    .filter((item) => {
      if (!isValidHoliday(item)) {
        // eslint-disable-next-line no-console
        console.warn('[holidays] Invalid holiday item skipped', item);
        return false;
      }
      return true;
    });
};

export default function useHolidays() {
  const [holidays, setHolidays] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadHolidays = () => {
      try {
        setIsLoading(true);
        const fallbackList = normalizeHolidayList(holidayFallback);
        setHolidays(fallbackList);
        setError(null);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[holidays] Failed to load holiday data', err);
        setError(err?.message || 'Failed to load holiday data');
        setHolidays([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadHolidays();
  }, []);

  const holidayLookup = useMemo(() => {
    const solar = new Map();
    const lunar = new Map();

    holidays.forEach((holiday) => {
      if (holiday.type === 'solar' && holiday.solarDate) {
        const list = solar.get(holiday.solarDate) || [];
        list.push(holiday);
        solar.set(holiday.solarDate, list);
      }

      if (holiday.type === 'lunar' && holiday.lunarDate) {
        const list = lunar.get(holiday.lunarDate) || [];
        list.push(holiday);
        lunar.set(holiday.lunarDate, list);
      }
    });

    return { solar, lunar };
  }, [holidays]);

  return {
    holidays,
    holidayLookup,
    isLoading,
    error
  };
}
